import React, { useEffect } from 'react'
import * as yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'
import useFormWithErrors from '@hooks/useFormWithErrors'

import { Button } from '@atoms'

import EditableTextFieldRow from './EditableTextFieldRow'
import useSupporterUpdateMutation from './hooks/useSupporterUpdateMutation'
import { scopedTranslation } from '@utils/I18n'
import { useSupporterReload } from './SupporterReloadContext'

const tShared = scopedTranslation('shared')
const tAttributes = scopedTranslation('attributes')

type LastNameFieldRowType = {
  supporterId: string | number
  canEdit?: boolean
  lastName: string | null
}

export default function LastNameFieldRow({ supporterId, canEdit, lastName }: LastNameFieldRowType) {
  const [open, setOpen] = React.useState(false)
  const reloadSupporter = useSupporterReload()

  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const editButtonRef = React.useRef<HTMLButtonElement | null>(null)

  const validations = yup.object().shape({
    supporter: yup.object({
      lastName: yup.string().label(tAttributes('supporter.last_name')),
    }),
  })

  const {
    setErrors: setFormErrors,
    register,
    handleSubmit,
    control,
    setValue: setFormValue,
  } = useFormWithErrors({
    resolver: yupResolver(validations),
    mode: 'onTouched',
    defaultValues: {
      supporter: { lastName: lastName ?? '' },
    },
  })

  const inputProps = register('supporter.lastName')

  useEffect(() => {
    setFormValue('supporter.lastName', lastName ?? '')
  }, [lastName, setFormValue])

  const { onSubmit } = useSupporterUpdateMutation({
    setFormErrors,
    supporterId,
    onSuccess: () => {
      setOpen(false)
      reloadSupporter()
      editButtonRef.current?.focus()
    },
  })

  const onCancel = () => {
    setOpen(false)
    setTimeout(() => {
      editButtonRef.current?.focus()
    })
  }

  const onEdit = () => {
    setOpen(true)
    setTimeout(() => {
      inputRef.current?.focus()
    })
  }

  return (
    <div
      className="tw:grid tw:grid-cols-5 tw:grid-rows-1 tw:gap-4 tw:w-full tw:py-3 align-items-center"
      data-compid="supporter-show_last_name-field-row"
    >
      <div>
        <p className="tw:m-0 tw:text-gray-900 tw:font-medium tw:text-sm">{tAttributes('supporter.last_name')}</p>
      </div>
      {open ? (
        <div className="tw:col-span-2">
          <EditableTextFieldRow
            {...inputProps}
            onSubmit={handleSubmit(onSubmit)}
            label={tAttributes('supporter.last_name')}
            open={open}
            control={control}
            ref={(el) => {
              inputRef.current = el
              inputProps.ref(el)
            }}
            onCancel={onCancel}
          />
        </div>
      ) : (
        <div className="tw:col-span-3">
          <p>{lastName}</p>
        </div>
      )}

      <div className="tw:col-start-5 tw:flex tw:justify-end">
        <Button
          rank="secondary"
          leadingIcon="pencil-02"
          onClick={onEdit}
          ref={editButtonRef}
          disabled={canEdit ? open : true}
          ariaLabel={tShared('aria_labels.edit_object', { name: tAttributes('supporter.last_name') })}
          dataCompId="supporter-show_last_name-field-row_edit"
        >
          {tShared('buttons.edit')}
        </Button>
      </div>
    </div>
  )
}
